/**
 * useAgentProgress — React hook that polls /api/agent-progress for a target
 * the user is watching (a document, a memo, an NA file, a policy, etc.).
 *
 * Returns the ordered list of step events. Polls every 2 seconds while the
 * latest event's status is 'started'. Stops polling once the latest event
 * is 'completed' or 'failed'. Pauses while the tab is hidden.
 *
 * Usage:
 *   const { events, status } = useAgentProgress('memo', memoId);
 *   <AgentThinkingPanel events={events} status={status} />
 */
import { useEffect, useRef, useState } from 'react';

const API = process.env.REACT_APP_API_URL;
const POLL_MS = 2000;

function latestStatus(events) {
  if (!events || events.length === 0) return 'idle';
  const last = events[events.length - 1];
  return last.step_status || 'idle';
}

export default function useAgentProgress(targetType, targetId) {
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState('idle');
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (!targetType || !targetId) return undefined;

    let cancelled = false;
    stoppedRef.current = false;

    async function tick() {
      if (cancelled || stoppedRef.current) return;
      if (document.hidden) {
        // Tab not visible — defer polling.
        setTimeout(tick, POLL_MS);
        return;
      }
      try {
        const url = `${API}/api/agent-progress/${encodeURIComponent(targetType)}/${encodeURIComponent(targetId)}`;
        const r = await fetch(url, { credentials: 'include' });
        if (!cancelled && r.ok) {
          const data = await r.json();
          if (data?.events) {
            setEvents(data.events);
            const s = latestStatus(data.events);
            setStatus(s);
            if (s === 'completed' || s === 'failed') {
              stoppedRef.current = true;
              return; // Stop polling.
            }
          }
        }
      } catch (_) {
        // Network blip — keep trying.
      }
      if (!cancelled && !stoppedRef.current) {
        setTimeout(tick, POLL_MS);
      }
    }

    tick();

    return () => {
      cancelled = true;
      stoppedRef.current = true;
    };
  }, [targetType, targetId]);

  return { events, status };
}
